import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/utils/api';
import { Plus, AlertTriangle, Clock } from 'lucide-react';
import Layout from '@/components/Layout';
import ModuleIntelligenceBlock from '@/components/ModuleIntelligenceBlock';

const statusColors = {
  active: 'bg-green-100 text-green-800',
  expired: 'bg-red-100 text-red-800',
  terminated: 'bg-gray-100 text-gray-800'
};

export default function Leases() {
  const { t } = useTranslation();
  const [leases, setLeases] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [units, setUnits] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    tenant_id: '', unit_id: '', property_id: '', start_date: '',
    end_date: '', monthly_rent: '', security_deposit: '', payment_day: '1', status: 'active'
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [leasesRes, tenantsRes, unitsRes, propsRes] = await Promise.all([
        api.getLeases(), api.getTenants(), api.getUnits(), api.getProperties()
      ]);
      setLeases(leasesRes.data);
      setTenants(tenantsRes.data);
      setUnits(unitsRes.data);
      setProperties(propsRes.data);
    } catch (error) { console.error('Failed:', error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const unit = units.find(u => u.id === formData.unit_id);
    try {
      await api.createLease({
        ...formData,
        property_id: unit?.property_id || formData.property_id,
        monthly_rent: parseFloat(formData.monthly_rent),
        security_deposit: parseFloat(formData.security_deposit),
        payment_day: parseInt(formData.payment_day),
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString()
      });
      setDialogOpen(false);
      setFormData({ tenant_id: '', unit_id: '', property_id: '', start_date: '', end_date: '', monthly_rent: '', security_deposit: '', payment_day: '1', status: 'active' });
      fetchData();
    } catch (error) { console.error('Failed to save:', error); }
  };

  const getTenantName = (id) => tenants.find(t => t.id === id)?.full_name || '-';
  const getUnitLabel = (id) => {
    const unit = units.find(u => u.id === id);
    return unit ? `${unit.unit_number} (${unit.unit_type})` : '-';
  };

  const isExpiringSoon = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const daysLeft = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return daysLeft <= 30 && daysLeft > 0;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold">{t('leases')}</h1>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button><Plus className="mr-2 h-4 w-4" /> {t('addLease')}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('addLease')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t('tenants')}*</Label>
                    <Select value={formData.tenant_id} onValueChange={(v) => setFormData({ ...formData, tenant_id: v })}>
                      <SelectTrigger><SelectValue placeholder="Select tenant" /></SelectTrigger>
                      <SelectContent>
                        {tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.full_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('units')}*</Label>
                    <Select value={formData.unit_id} onValueChange={(v) => {
                      const unit = units.find(u => u.id === v);
                      setFormData({ ...formData, unit_id: v, property_id: unit?.property_id || '', monthly_rent: unit?.monthly_rent?.toString() || formData.monthly_rent });
                    }}>
                      <SelectTrigger><SelectValue placeholder="Select unit" /></SelectTrigger>
                      <SelectContent>
                        {units.filter(u => u.status === 'vacant').map(u => (
                          <SelectItem key={u.id} value={u.id}>{u.unit_number} - {u.unit_type} ({u.monthly_rent} SAR)</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t('startDate')}*</Label>
                    <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} required />
                  </div>
                  <div>
                    <Label>{t('endDate')}*</Label>
                    <Input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} required />
                  </div>
                  <div>
                    <Label>{t('monthlyRent')} (SAR)*</Label>
                    <Input type="number" value={formData.monthly_rent} onChange={(e) => setFormData({ ...formData, monthly_rent: e.target.value })} required />
                  </div>
                  <div>
                    <Label>{t('securityDeposit')} (SAR)*</Label>
                    <Input type="number" value={formData.security_deposit} onChange={(e) => setFormData({ ...formData, security_deposit: e.target.value })} required />
                  </div>
                  <div>
                    <Label>{t('paymentDay')}*</Label>
                    <Input type="number" min="1" max="28" value={formData.payment_day} onChange={(e) => setFormData({ ...formData, payment_day: e.target.value })} required />
                  </div>
                </div>
                <Button type="submit" className="w-full">{t('save')}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4">
          <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-green-600">{leases.filter(l => l.status === 'active').length}</div><div className="text-sm text-gray-500">{t('active')}</div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-yellow-600">{leases.filter(l => isExpiringSoon(l.end_date)).length}</div><div className="text-sm text-gray-500">Expiring Soon</div></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-red-600">{leases.filter(l => l.status === 'expired').length}</div><div className="text-sm text-gray-500">{t('expired')}</div></CardContent></Card>
        </div>

        <Card>
          <CardContent className="pt-6">
            {loading ? <div className="text-center py-8">Loading...</div> : leases.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No leases yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('tenants')}</TableHead>
                    <TableHead>{t('units')}</TableHead>
                    <TableHead>{t('monthlyRent')}</TableHead>
                    <TableHead>{t('startDate')}</TableHead>
                    <TableHead>{t('endDate')}</TableHead>
                    <TableHead>{t('status')}</TableHead>
                    <TableHead>Alert</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leases.map((lease) => (
                    <TableRow key={lease.id}>
                      <TableCell className="font-medium">{getTenantName(lease.tenant_id)}</TableCell>
                      <TableCell>{getUnitLabel(lease.unit_id)}</TableCell>
                      <TableCell>{lease.monthly_rent} SAR</TableCell>
                      <TableCell>{formatDate(lease.start_date)}</TableCell>
                      <TableCell>{formatDate(lease.end_date)}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[lease.status]}`}>{t(lease.status)}</span>
                      </TableCell>
                      <TableCell>
                        {isExpiringSoon(lease.end_date) && (
                          <span className="flex items-center gap-1 text-yellow-600 text-xs">
                            <Clock className="h-3 w-3" /> Expiring Soon
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <ModuleIntelligenceBlock module="leases" testId="leases-intel" />
      </div>
    </Layout>
  );
}
